import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Slider from 'Components/DesignSystem/Slider/Slider';
import SidePanel from 'Components/DesignSystem/SidePanel/SidePanel';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';
import Spinner from 'Components/DesignSystem/Spinner/Spinner';
import { Cross } from 'Components/DesignSystem/Iconography/Icons';
import { getWeightageConfig, updateWeightageConfig } from '../services/CategoryRelevancyServices';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    channelKey: string;
    // Fires after a successful PUT weightage_config. The parent uses this
    // to flip the page into the "compute in progress" state so polling
    // re-arms fast and the eventual `last_computed_at` advancement fires
    // a table refetch — without this signal, polling stays at the 10-min
    // idle cadence and the UI sits on stale unified_score values until
    // the next slow tick.
    onWeightageSaved?: () => void;
};

const ManageUnifiedScoringPanel: React.FC<Props> = ({ isOpen, onClose, channelKey, onWeightageSaved }) => {
    const [invoiceWeight, setInvoiceWeight] = useState(50);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const attributeWeight = 100 - invoiceWeight;

    useEffect(() => {
        if (!isOpen || !channelKey) return;

        setIsLoading(true);
        getWeightageConfig(channelKey)
            .then((data) => {
                if (data?.data) {
                    const invoiceW = Math.round((data.data.invoice_weight ?? 0.5) * 100);
                    setInvoiceWeight(invoiceW);
                }
            })
            .catch(() => {
                // silently use defaults
            })
            .finally(() => setIsLoading(false));
    }, [isOpen, channelKey]);

    const saveWeightage = useCallback(
        async (invoice: number) => {
            if (!channelKey) return;
            setIsSaving(true);
            try {
                await updateWeightageConfig(channelKey, invoice / 100, (100 - invoice) / 100);
                onWeightageSaved?.();
            } catch {
                toast.error('Failed to save weightage configuration');
            } finally {
                setIsSaving(false);
            }
        },
        [channelKey, onWeightageSaved]
    );

    /* `Slider` hands back the number; the 600ms debounce below is untouched. */
    const handleSliderChange = (val: number) => {
        setInvoiceWeight(val);
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => saveWeightage(val), 600);
    };

    const handlePreset = (invoice: number) => {
        setInvoiceWeight(invoice);
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => saveWeightage(invoice), 600);
    };

    /* The shell is the DS SidePanel (md = the historical 416px): it owns the
       scrim, presence, Escape-to-close, header and body chrome. `tabs={[]}` is
       load-bearing — omitting it renders the legacy Environment tab set (§ 53).
       The close X rides `headerActions` so it keeps this panel's historical
       testid; the panel has no Save action (the slider autosaves on a 600ms
       debounce), so the legacy Cancel/Save default must not render. */
    return (
        <SidePanel
            isOpen={isOpen}
            onClose={onClose}
            title="Manage Unified Scoring"
            tabs={[]}
            width="md"
            data-testid="components-manageunifiedscoringpanel"
            headerActions={
                <IconButton
                    aria-label="Close"
                    size="sm"
                    onClick={onClose}
                    data-testid="components-manageunifiedscoringpanel-button-1"
                >
                    <Cross size={16} />
                </IconButton>
            }
        >
            {isLoading ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-h-32">
                    <Spinner size="sm" label="Loading..." showLabel />
                </div>
            ) : (
                <>
                    {/* Section label */}
                    <p className="tw-text-sm tw-text-ink-secondary tw-mb-6 tw-mt-2">
                        Weight distribution
                    </p>

                    {/* Slider */}
                    <div className="tw-mb-3">
                        {/*
                          The control had NO name — not a label, not an
                          `aria-label`, nothing. "Weight distribution" above
                          it is a section caption for the whole block, and the
                          two percentage boxes below it are read-only output,
                          so there was nothing for a screen reader to tie the
                          slider to. `hideLabel` names it without changing the
                          layout the caption already establishes.
                        */}
                        <Slider
                            label="Invoice weight"
                            hideLabel
                            formatValue={(percentage) => `${percentage}%`}
                            data-testid="category-relevancy-invoice-weight-slider"
                            value={invoiceWeight}
                            onChange={handleSliderChange}
                            min={0}
                            max={100}
                        />
                    </div>

                    {/* Percentage boxes + labels */}
                    <div className="tw-flex tw-items-start tw-justify-between tw-mb-8">
                        <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
                            <div className="tw-border tw-border-solid tw-border-edge-strong tw-rounded tw-px-3 tw-py-1 tw-text-sm tw-font-semibold tw-text-ink tw-min-w-[56px] tw-text-center">
                                {invoiceWeight}%
                            </div>
                            <span className="tw-text-xs tw-text-ink-secondary">Invoice Weight</span>
                        </div>

                        <div className="tw-flex tw-flex-col tw-items-end tw-gap-1">
                            <div className="tw-border tw-border-solid tw-border-edge-strong tw-rounded tw-px-3 tw-py-1 tw-text-sm tw-font-semibold tw-text-ink tw-min-w-[56px] tw-text-center">
                                {attributeWeight}%
                            </div>
                            <span className="tw-text-xs tw-text-ink-secondary">Attribute Weight</span>
                        </div>
                    </div>

                    {isSaving && (
                        <p className="tw-text-xs tw-text-ink-tertiary tw-text-center">
                            Saving...
                        </p>
                    )}

                    {/* Preset buttons */}
                    <div className="tw-mt-6">
                        <p className="tw-text-xs tw-text-ink-secondary tw-mb-3">Presets</p>
                        <div className="tw-flex tw-gap-2">
                            {([
                                { label: '70-30', invoice: 70, description: 'Invoice Heavy' },
                                { label: '50-50', invoice: 50, description: 'Balanced' },
                                { label: '30-70', invoice: 30, description: 'Overlap Heavy' },
                            ] as const).map(({ label, invoice, description }) => (
                                <button data-testid="components-manageunifiedscoringpanel-button-2"
                                    key={label}
                                    onClick={() => handlePreset(invoice)}
                                    className={`tw-flex-1 tw-flex tw-flex-col tw-items-center tw-gap-0.5 tw-border tw-border-solid tw-rounded tw-py-2 tw-px-1 tw-text-xs tw-transition-colors ${
                                        invoiceWeight === invoice
                                            ? 'tw-border-accent tw-bg-accent-tint tw-text-accent'
                                            : 'tw-border-edge-strong tw-bg-surface tw-text-ink-secondary hover:tw-border-accent'
                                    }`}
                                >
                                    <span className="tw-font-semibold">{label}</span>
                                    <span className="tw-text-[10px]">{description}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </SidePanel>
    );
};

export default ManageUnifiedScoringPanel;
